import React, { useState, useEffect } from "react"

//controls
import { Button, Col, Row, Label } from "reactstrap"

//react form
import { FormProvider, useForm } from "react-hook-form"

//navigation
import { Link, useNavigate, useParams } from "react-router-dom"

//validation
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"

//redux
import { useSelector, useDispatch } from "react-redux"

//custom hooks
import { useDeepCompareEffect, useUserDetail } from "hooks"

//Date filter
import Moment from "moment"

import {
  addNewUser as onAddNewUser,
  getRoleTypes as onGetRoleTypes,
  updateUser as onUpdateUser,
  updateUserPhoto as onUpdateUserPhoto,
  resetUser as onResetUser,
  getSpecializations as onGetSpecializations,
  getGenders as onGetGenders,
  getNNS as onGetNNS,
} from "store/actions"
import LocationField from "components/Common/Location/Location"
import PhotoField from "components/Common/Crop/PhotoField"
import Spinners from "components/Common/Spinner"
import { getUserImagePath } from "../../../helpers/imageService"
import { isEmpty } from "lodash"
import { EnumRoleTypes } from "helpers/enum_helper"

import {
  TextInput,
  DropdownInput,
  NumberInput,
  DateInput,
  ToggleInput,
} from "components/Common/Controls"
import AlertContainer from "components/Common/AlertContainer"
import { useRole } from "hooks/useRole"

function BasicInfoTab({ user, loading, myprofile = false }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(loading)
  const { isAdmin, isParticipant, isHost, roleLabel } = useRole()

  /**
   * Form Validation Schema
   */
  const schema = yup.object().shape({
    FirstName: yup
      .string()
      .required("Required")
      .min(3, "First name must be at least 3 characters"),
    LastName: yup.string().required("Required"),
    // LoginUserName: yup.string().required("Required"),
    IsActive: yup.bool(),
    Email: yup.string().required("Required").email("Must be a valid Email"),
    PhoneNumber: yup.string().nullable(),
    DateOfBirth: yup
      .string()
      .required("Required")
      .test("DateOfBirth", "Should be above 18 Years", value => {
        return value !== "" ? Moment().diff(Moment(value), "years") >= 18 : true
      }),
    Address: yup.string(),
    RoleTypes: yup.object().required("Required"),
    Countries: yup.object(),
    States: yup.object(),
    Cities: yup.object(),
    ZipCode: yup.string().nullable(),
    Specializations: yup.object().nullable(),
    Photo: yup
      .mixed()
      .nullable(true)
      .transform((_, val) => (val ? String(val) : null)),
    Genders: yup.object().required("Required"),
    AreaName: yup.string().nullable(),
  })

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      LoginUserName: "",
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNumber: "",
      DateOfBirth: "",
      Address: "",
      ZipCode: "",
      Photo: "",
      IsActive: true,
      AreaName: "",
    },
    resolver: yupResolver(schema),
  })
  const { reset, control, formState, setValue, getValues, trigger, watch } =
    methods
  const { errors, isValid } = formState

  useDeepCompareEffect(() => {
    function updateUserState() {
      dispatch(onGetRoleTypes())
      dispatch(onGetSpecializations())
      dispatch(onGetGenders())
      dispatch(onGetNNS())

      if (isEmpty(user)) {
        var roleTypes = {}
        if (isHost) {
          roleTypes = {
            RoleTypeID: EnumRoleTypes.Administrator,
            RoleType: "Administrator",
          }
        }
        if (isAdmin) {
          roleTypes = {
            RoleTypeID: EnumRoleTypes.Participant,
            RoleType: "Participant",
          }
        }
        reset({
          ...user,
          RoleTypes: roleTypes,
        })
      } else {
        //reset form with server data
        reset(user)
      }
    }

    updateUserState()
  }, [dispatch])

  //////////////////ROLE STATE/////////////////////
  const { roles, roletypes } = useSelector(state => state.role)
  ///////////////////////////////////////

  //////////////////HELPER STATE/////////////////////
  const { specializations } = useSelector(state => state.helper)
  ///////////////////////////////////////

  //////////////////STAFF STATE/////////////////////
  const { genders, error } = useSelector(state => state.user)
  ///////////////////////////////////////

  //////////////////PROJECT STATE/////////////////////
  const { nns } = useSelector(state => state.project)
  ///////////////////////////////////////

  useEffect(() => {
    return () => {
      dispatch(onResetUser())
    }
  }, [dispatch])

  const onCancelClick = () => {
    navigate("/users")
  }

  const handleSave = () => {
    trigger() //for form validation
    if (isValid) {
      if (isEmpty(user)) {
        dispatch(onAddNewUser(getValues()))
      } else {
        dispatch(onUpdateUserPhoto(getValues()))
        dispatch(onUpdateUser(getValues()))
      }
      if (!isEmpty(id)) {
        navigate("/users")
      }
    }
  }

  if (isLoading) {
    return <Spinners setLoading={setLoading} />
  }

  return (
    <FormProvider {...methods}>
      <Row>
        <Col sm="4" className="order-md-3">
          <div className="mb-3">
            <PhotoField
              url={getUserImagePath(user?.RowID, user?.Photo ?? "")}
            />
          </div>
        </Col>

        <Col sm="8">
          <Row>
            <Col sm="6">
              <div hidden={isEmpty(user)} className="mb-3">
                <Label>Username</Label>
                <div>
                  {user.RoleTypeID === parseInt(EnumRoleTypes.Host) ||
                  user.RoleTypeID === parseInt(EnumRoleTypes.Administrator)
                    ? getValues("LoginUserName")
                    : "NA"}
                </div>
              </div>
            </Col>
            <Col sm="6">
              <div hidden={isEmpty(user)} className="mb-3">
                <Label>Reference Number</Label>
                <div>{"Citizen " + user.UserID}</div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm="6">
              <div className="mb-3">
                <TextInput label="First Name" id="FirstName" required={true} />
              </div>
            </Col>
            <Col sm="6">
              <div className="mb-3">
                <TextInput label="Last Name" id="LastName" required={true} />
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm="6">
              <div className="mb-3">
                <TextInput label="Email" id="Email" required={true} />
              </div>
            </Col>
            <Col sm="6">
              <div className="mb-3">
                <NumberInput
                  label="Phone Number"
                  id="PhoneNumber"
                  required={true}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm="6">
              <div className="mb-3">
                <DropdownInput
                  label="Role"
                  id="RoleTypes"
                  required={true}
                  items={roletypes.filter(x => x.IsSystem == 0)}
                  itemlabel="RoleType"
                  itemvalue="RoleTypeID"
                />
              </div>
            </Col>
            <Col sm="6">
              <div className="mb-3">
                <DropdownInput
                  label="Gender"
                  id="Genders"
                  required={true}
                  items={genders}
                  itemlabel="Gender"
                  itemvalue="GenderID"
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm="6">
              <div className="mb-3">
                <DateInput
                  label="Date of Birth"
                  id="DateOfBirth"
                  required={true}
                />
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col sm="8">
          <LocationField />
        </Col>
      </Row>
      <Row>
        <Col sm="4">
          <div className="mb-3">
            <ToggleInput label="Active" id="IsActive" />
          </div>
        </Col>
      </Row>

      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" className="btn" onClick={handleSave}>
          {isEmpty(user) ? "Save" : "Update"}
        </Button>
        <Button
          type="submit"
          color="secondary"
          onClick={onCancelClick}
          hidden={myprofile}
        >
          Cancel
        </Button>
      </div>
    </FormProvider>
  )
}
export default BasicInfoTab
